import { useContext, useEffect, useRef } from 'react';
import { gql, useQuery } from '@apollo/client';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { maybeInitSentry } from '@/sentry';

const GET_USER_DETAILS = gql`
  query GetUserDetails {
    userDetails {
      defaultS3Endpoint
      defaultDevoEndpoint
      features {
        agentCollaboration
        automationsToolsEnabled
        caseManagementToolsEnabled
        concreteModelSelection
        constructIntegration
        customAgents
        gitlabToolsEnabled
        knowledgebasesEdit
        knowledgebasesViewOnly
        mcpManagement
        mcpToolSelection
        secOpsToolsEnabled
        secretsManagementEnabled
        webhooksEnabled
        workflowBuilderEnabled
        externalConnectors
        workflowsManage
        sentryUnmaskData
        showAlphaMcpServers
        showBetaMcpServers
        showDeprecatedMcpServers
        asocV2Enabled
        dataRoutingEnabled
        rbacEnabled
        rbvcEnabled
        tokenPricing
        tokenUsageAdmin
        tokenUsageMonitor
        strikekitEnabled
        overviewEnabled
        customersViewDashboards
        editDashboards
        organizationManagement
        systemAdministration
        snippetsManager
        codeModeEnabled
        merovingianEnabled
        documentSharingEnabled
        tenantWindowsEnabled
      }
      rbvcAllowedPersonaIds
      rbvcAllowedFeatureIds
    }
  }
`;

interface UserDetailsResponse {
  userDetails: {
    defaultS3Endpoint?: string | null;
    defaultDevoEndpoint?: string | null;
    features: {
      agentCollaboration: boolean;
      automationsToolsEnabled: boolean;
      caseManagementToolsEnabled: boolean;
      concreteModelSelection: boolean;
      constructIntegration: boolean;
      customAgents: boolean;
      gitlabToolsEnabled: boolean;
      knowledgebasesEdit: boolean;
      knowledgebasesViewOnly: boolean;
      mcpManagement: boolean;
      mcpToolSelection: boolean;
      secOpsToolsEnabled: boolean;
      secretsManagementEnabled: boolean;
      webhooksEnabled: boolean;
      workflowBuilderEnabled: boolean;
      externalConnectors: boolean;
      workflowsManage: boolean;
      sentryUnmaskData: boolean;
      showAlphaMcpServers: boolean;
      showBetaMcpServers: boolean;
      showDeprecatedMcpServers: boolean;
      asocV2Enabled: boolean;
      dataRoutingEnabled: boolean;
      rbacEnabled: boolean;
      rbvcEnabled: boolean;
      tokenPricing: boolean;
      tokenUsageAdmin: boolean;
      tokenUsageMonitor: boolean;
      strikekitEnabled: boolean;
      overviewEnabled: boolean;
      customersViewDashboards: boolean;
      editDashboards: boolean;
      organizationManagement: boolean;
      systemAdministration: boolean;
      snippetsManager: boolean;
      codeModeEnabled: boolean;
      merovingianEnabled: boolean;
      documentSharingEnabled: boolean;
      tenantWindowsEnabled: boolean;
    };
    rbvcAllowedPersonaIds: string[] | null;
    rbvcAllowedFeatureIds: string[] | null;
  };
}

/**
 * Hook to fetch and update user details with USER-SPECIFIC features.
 * This fetches features that are specific to the authenticated user.
 *
 * Note: Public features (like auth method flags) are handled separately
 * by usePublicFeatures hook and are not user-specific.
 */
export const useUserDetailsWithFeatures = () => {
  const { userInfo, setUserInfo } = useContext(UserInfoContext);
  const hasUpdatedFeatures = useRef(false);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    if (userInfo?.id !== lastUserId.current) {
      hasUpdatedFeatures.current = false;
      lastUserId.current = userInfo?.id ?? null;
    }
  }, [userInfo?.id]);

  // Skip if no userInfo (not authenticated)
  // Auth is via session cookie, so we just check if userInfo exists
  const shouldSkip = !userInfo?.id;

  const { data, loading, error } = useQuery<UserDetailsResponse>(GET_USER_DETAILS, {
    skip: shouldSkip, // Only fetch if user is authenticated
    fetchPolicy: 'cache-and-network', // Always check server for fresh features
    // Background poll every 60s so RBVC grant/revoke and other server-side
    // session changes propagate even when the user stays on a single tab.
    // Cheap: userDetails is a single-row read.
    pollInterval: 60_000,
  });

  // RBVC: keep `rbvcAllowedPersonaIds` in sync on every userDetails refetch
  // (cache-and-network fires on remount, focus, manual refetch, etc).
  // Unlike feature flags — which are locked after first set to avoid mid-
  // session toggling — RBVC role assignments are expected to change while
  // a member is logged in (admin granting/revoking) and the sidebar must
  // reflect the latest state without forcing a re-login.
  useEffect(() => {
    if (!data?.userDetails || !userInfo) return;
    const fresh = data.userDetails.rbvcAllowedPersonaIds ?? null;
    const current = userInfo.rbvcAllowedPersonaIds ?? null;
    // Compare nullable arrays as SETS — server may return ids in any order
    // (e.g. distinct() doesn't guarantee row order). An order-only diff
    // would treat [a,b] ≠ [b,a] and trigger setUserInfo every poll, which
    // re-runs this effect → infinite render loop. Set-membership is the
    // correct semantic match: ids are unique uuids, position is irrelevant.
    if (fresh === null && current === null) return;
    if (Array.isArray(fresh) && Array.isArray(current)) {
      if (fresh.length === current.length) {
        const currentSet = new Set(current);
        if (fresh.every(id => currentSet.has(id))) return;
      }
    }
    setUserInfo({ ...userInfo, rbvcAllowedPersonaIds: fresh });
  }, [data?.userDetails?.rbvcAllowedPersonaIds, userInfo, setUserInfo]);

  // Same set-based sync for the feature allow-list. Mirrors the persona
  // path: poll catches admin grants/revokes mid-session without a
  // re-login, but the diff-as-set protects against a render loop on
  // server reorderings.
  useEffect(() => {
    if (!data?.userDetails || !userInfo) return;
    const fresh = data.userDetails.rbvcAllowedFeatureIds ?? null;
    const current = userInfo.rbvcAllowedFeatureIds ?? null;
    if (fresh === null && current === null) return;
    if (Array.isArray(fresh) && Array.isArray(current)) {
      if (fresh.length === current.length) {
        const currentSet = new Set(current);
        if (fresh.every(id => currentSet.has(id))) return;
      }
    }
    setUserInfo({ ...userInfo, rbvcAllowedFeatureIds: fresh });
  }, [data?.userDetails?.rbvcAllowedFeatureIds, userInfo, setUserInfo]);

  useEffect(() => {
    // Only update features once when data is first available
    if (data?.userDetails?.features && userInfo && !hasUpdatedFeatures.current) {
      // Merge fresh user-specific features with existing public features
      // Preserve public features (devo/keycloak auth) that may have been set during login
      const mergedFeatures = {
        ...userInfo.features, // Keep existing features (including public ones)
        ...data.userDetails.features, // Override with fresh user-specific features
        // Ensure all boolean fields are properly defined
        gitlabToolsEnabled:
          data.userDetails.features.gitlabToolsEnabled ??
          userInfo.features?.gitlabToolsEnabled ??
          false,
      };

      setUserInfo({
        ...userInfo,
        features: mergedFeatures,
        // rbvcAllowedPersonaIds is updated by the dedicated effect above
        // so revocations propagate without re-login.
        defaultS3Endpoint: data.userDetails.defaultS3Endpoint ?? userInfo.defaultS3Endpoint ?? null,
        defaultDevoEndpoint: data.userDetails.defaultDevoEndpoint ?? userInfo.defaultDevoEndpoint ?? null,
      });
      maybeInitSentry(mergedFeatures.sentryUnmaskData);

      hasUpdatedFeatures.current = true;
    }
  }, [data, userInfo, setUserInfo]);

  return { loading, error, features: data?.userDetails?.features };
};
